/* ============================================================================
   SG-2026 · Shopify Shipping-Rates API Wrapper
   ----------------------------------------------------------------------------
   Ruft die echten, live von Shopify berechneten Versandkosten ab — basierend
   auf den im Admin konfigurierten Versandprofilen (Gewichtsstaffeln, Zonen,
   Paket/Spedition, Gefahrgut).

   Performance-Strategie:
     1. Prefetch: Sobald der Cart sich aendert, feuert ein Rate-Request ins
        Leere — das Ergebnis landet im Cache.
     2. SessionStorage-Cache: Key = Hash(cart.token + items + total). Bei
        gleichem Cart = instant aus Cache (0 ms).
     3. Stale-while-revalidate: Anzeige nutzt Cache sofort, revalidiert im
        Hintergrund wenn > 60s alt.
     4. Request-Dedup: Nur 1 Inflight-Request pro Cart-Hash (auch wenn 5
        Komponenten gleichzeitig ratenwollen).
     5. Graceful Fallback: Bei Fehler/Timeout -> stille Rueckgabe `null`,
        UI zeigt Default-Text.

   Public API:
     window.sgShippingRates.getRates(opts)  -> Promise<Rates|null>
     window.sgShippingRates.prefetch()      -> fire-and-forget
     window.sgShippingRates.clearCache()    -> void
     Events: 'sg:shipping-rates:update'     (CustomEvent, detail.rates)
   ============================================================================ */

(function () {
  'use strict';

  if (window.sgShippingRates) return; // Singleton

  var CACHE_KEY   = 'sg-shipping-rates-cache-v1';
  var CACHE_TTL   = 10 * 60 * 1000; // 10 min — laenger nicht sinnvoll (Kurs, Staffeln koennten sich aendern)
  var STALE_AFTER = 60 * 1000;      // 1 min — revalidate im Hintergrund
  var FETCH_TIMEOUT_MS = 6000;      // 6s harter Cutoff
  var MAX_RETRIES = 1;

  function getStorage() {
    try {
      var key = '__sg_test_' + Date.now();
      window.sessionStorage.setItem(key, '1');
      window.sessionStorage.removeItem(key);
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  /**
   * Stabiler Hash aus den Cart-Daten die relevant fuer Versandkosten sind.
   * Keine kryptografische Sicherheit noetig — muss nur eindeutig pro "Ship-State"
   * sein. Benutzt cart.token + gewicht + items (variant_id+qty).
   */
  function hashCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return 'empty';
    var items = cart.items
      .map(function (it) { return (it.variant_id || it.id) + ':' + it.quantity + ':' + (it.grams || 0); })
      .join('|');
    return (cart.token || 'anon') + ':' + cart.total_weight + ':' + items;
  }

  /**
   * Einfache djb2-ish hash fuer kurzen Cache-Key.
   */
  function shortHash(str) {
    var h = 5381;
    for (var i = 0; i < str.length; i++) {
      h = ((h << 5) + h) + str.charCodeAt(i);
      h = h & h;
    }
    return String(h >>> 0);
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout')); }, ms);
      promise.then(
        function (v) { clearTimeout(timer); resolve(v); },
        function (e) { clearTimeout(timer); reject(e); }
      );
    });
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getDefaultAddress() {
    // Theme-Setting via <script> data-sg-shipping-default-zip / data-sg-shipping-default-country
    var el = document.querySelector('script[data-sg-shipping-config]');
    var zip = el && el.dataset.sgShippingDefaultZip;
    var country = el && el.dataset.sgShippingDefaultCountry;

    // Fallbacks: Shopify.country global, oder DE
    if (!country && window.Shopify && window.Shopify.country) {
      country = window.Shopify.country;
    }
    return {
      country: country || 'DE',
      zip: zip || '10115' // Berlin Mitte als unverfaenglicher Default
    };
  }

  /**
   * Shipping-Rates API Call. Versucht zuerst den synchronen Endpoint, fallt
   * dann auf den Async-Endpoint mit Polling zurueck falls noetig.
   */
  function fetchRatesOnce(address) {
    var params = new URLSearchParams();
    params.set('shipping_address[country]', address.country);
    if (address.zip) params.set('shipping_address[zip]', address.zip);
    if (address.province) params.set('shipping_address[province]', address.province);

    var url = routesRoot() + 'cart/shipping_rates.json?' + params.toString();

    return withTimeout(
      fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      }),
      FETCH_TIMEOUT_MS
    ).then(function (response) {
      if (!response.ok) {
        if (response.status === 422) {
          return null;
        }
        throw new Error('shipping_rates ' + response.status);
      }
      return response.json();
    }).then(function (data) {
      if (!data) return null;
      var rates = data.shipping_rates || data.rates || [];
      if (!Array.isArray(rates) || rates.length === 0) return null;

      // Normalisieren: price als Zahl-String ("4.99") -> in cents umwandeln
      return rates.map(function (r) {
        var priceCents = 0;
        if (typeof r.price === 'string') {
          priceCents = Math.round(parseFloat(r.price) * 100);
        } else if (typeof r.price === 'number') {
          priceCents = Math.round(r.price * 100);
        }
        return {
          name: r.name || r.presentment_name || 'Standard',
          presentmentName: r.presentment_name || r.name,
          price: priceCents,
          priceRaw: r.price,
          code: r.code,
          source: r.source,
          deliveryRange: r.delivery_range || null,
          deliveryDays: r.delivery_days || []
        };
      });
    });
  }

  var _inflight = {};

  function readCache() {
    var storage = getStorage();
    if (!storage) return {};
    try {
      var raw = storage.getItem(CACHE_KEY);
      if (!raw) return {};
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }

  function writeCache(map) {
    var storage = getStorage();
    if (!storage) return;
    try {
      storage.setItem(CACHE_KEY, JSON.stringify(map));
    } catch (e) {
      // Storage voll — aeltesten Eintrag loeschen
      try {
        var keys = Object.keys(map);
        if (keys.length > 1) {
          keys.sort(function (a, b) { return map[a].ts - map[b].ts; });
          delete map[keys[0]];
          storage.setItem(CACHE_KEY, JSON.stringify(map));
        }
      } catch (ee) { /* swallow */ }
    }
  }

  function getFromCache(key) {
    var map = readCache();
    var entry = map[key];
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL) {
      delete map[key];
      writeCache(map);
      return null;
    }
    return entry;
  }

  function putInCache(key, rates) {
    var map = readCache();
    map[key] = { rates: rates, ts: Date.now() };
    writeCache(map);
  }

  function dispatchUpdate(rates) {
    document.dispatchEvent(new CustomEvent('sg:shipping-rates:update', {
      detail: { rates: rates }
    }));
  }

  /**
   * Haupt-Entry.
   * opts.force : bool — Cache ignorieren
   * opts.silent: bool — kein Event feuern (Prefetch)
   * returns Promise<rates|null>
   */
  function getRates(opts) {
    opts = opts || {};

    return fetch(routesRoot() + 'cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) return null;
        if (cart.requires_shipping === false) return null;

        var addr = getDefaultAddress();
        var hashKey = shortHash(hashCart(cart) + ':' + addr.country + ':' + addr.zip);

        // Cache-Treffer?
        if (!opts.force) {
          var cached = getFromCache(hashKey);
          if (cached) {
            if (!opts.silent) dispatchUpdate(cached.rates);

            // Stale-while-revalidate: im Hintergrund neuen Call,
            // aber nur wenn Eintrag >60s alt ist
            if (Date.now() - cached.ts > STALE_AFTER && !_inflight[hashKey]) {
              _revalidate(hashKey, addr);
            }
            return cached.rates;
          }
        }

        // Request-Dedup
        if (_inflight[hashKey]) return _inflight[hashKey];

        var attempt = 0;
        function run() {
          attempt++;
          return fetchRatesOnce(addr).catch(function (e) {
            if (attempt <= MAX_RETRIES) {
              return new Promise(function (r) { setTimeout(r, 400); }).then(run);
            }
            throw e;
          });
        }

        _inflight[hashKey] = run()
          .then(function (rates) {
            delete _inflight[hashKey];
            if (rates) putInCache(hashKey, rates);
            if (!opts.silent) dispatchUpdate(rates);
            return rates;
          })
          .catch(function () {
            delete _inflight[hashKey];
            if (!opts.silent) dispatchUpdate(null);
            return null;
          });

        return _inflight[hashKey];
      })
      .catch(function () { return null; });
  }

  function _revalidate(key, addr) {
    _inflight[key] = fetchRatesOnce(addr)
      .then(function (rates) {
        delete _inflight[key];
        if (rates) {
          putInCache(key, rates);
          dispatchUpdate(rates);
        }
        return rates;
      })
      .catch(function () {
        delete _inflight[key];
        return null;
      });
  }

  function prefetch() {
    // Fire-and-forget — Event wird trotzdem dispatched damit offene
    // Komponenten reagieren koennen sobald Daten da sind.
    return getRates({ silent: false });
  }

  function clearCache() {
    var storage = getStorage();
    if (storage) storage.removeItem(CACHE_KEY);
  }

  window.sgShippingRates = {
    getRates: getRates,
    prefetch: prefetch,
    clearCache: clearCache,
    _hashCart: hashCart // fuer Debug
  };

  // Auto-Prefetch bei Cart-Aenderung
  function onCartChange() {
    // 120ms Delay: damit Shopify's interne Cart-Updates erst durchlaufen
    setTimeout(function () { prefetch(); }, 120);
  }

  document.documentElement.addEventListener('cart:change', onCartChange);
  document.addEventListener('cart:refresh', onCartChange);

  // Initial-Prefetch wenn Cart schon was enthaelt (Seiten-Reload)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(prefetch, 800); // erst nach initial render
    });
  } else {
    setTimeout(prefetch, 800);
  }
})();
